import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiError, apiFetch } from "@/lib/api";
import { CONTENT } from "@/content";
import { IconChevronL, IconWhats } from "@/components/admin/Icons";

type Lead = {
  id: number;
  nome: string;
  email: string;
  whatsapp: string;
  origem: string;
  status:
    | "novo"
    | "contatado"
    | "agendado"
    | "compareceu"
    | "no_show"
    | "convertido"
    | "descartado";
  prioridade: "baixa" | "media" | "alta";
  notas: string | null;
  created_at: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
};

type Answer = {
  question_idx: number;
  question_text: string | null;
  answer_text: string | null;
  answer_choice: string | null;
  answer_scale: number | null;
};

type Booking = {
  id: number;
  status: string;
  created_at: string;
  slot_id: number | null;
  data: string | null;
  dia_semana: string | null;
  inicio: string | null;
  fim: string | null;
} | null;

type SlotOption = {
  id: number;
  data: string;
  dia_semana: string;
  inicio: string;
  fim: string;
  capacidade: number;
  ativo: number;
  usados: number;
};

const STATUS_LABEL = {
  novo: "Novo",
  contatado: "Contatado",
  agendado: "Agendado",
  compareceu: "Compareceu",
  no_show: "No-show",
  convertido: "Convertido",
  descartado: "Descartado",
} as const;
type StatusKey = keyof typeof STATUS_LABEL;
const STATUS_ORDER: StatusKey[] = [
  "novo",
  "contatado",
  "agendado",
  "compareceu",
  "no_show",
  "convertido",
  "descartado",
];

const PRIORITY_LABEL = {
  baixa: "Baixa",
  media: "Media",
  alta: "Alta",
} as const;
type PriorityKey = keyof typeof PRIORITY_LABEL;
const PRIORITY_ORDER: PriorityKey[] = ["baixa", "media", "alta"];

function waUrl(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  return `https://wa.me/${digits.startsWith("55") ? digits : `55${digits}`}`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

function fmtTime(t: string | null): string {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  if (!hh) return t;
  const h = Number(hh);
  return mm && mm !== "00" ? `${h}h${mm}` : `${h}h`;
}

export default function AdminLeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [lead, setLead] = useState<Lead | null>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [booking, setBooking] = useState<Booking>(null);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [contactDraft, setContactDraft] = useState({ nome: "", email: "", whatsapp: "" });
  const [scheduleDate, setScheduleDate] = useState("");
  const [daySlots, setDaySlots] = useState<SlotOption[]>([]);
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [savingBooking, setSavingBooking] = useState(false);
  const [savedToast, setSavedToast] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    apiFetch<{ lead: Lead; answers: Answer[]; booking: Booking }>(
      `/admin/leads/${id}`,
      { auth: "admin" },
    )
      .then(({ lead, answers, booking }) => {
        setLead(lead);
        setAnswers(answers);
        setBooking(booking);
        setNotes(lead.notas ?? "");
        setContactDraft({ nome: lead.nome, email: lead.email, whatsapp: lead.whatsapp });
        setScheduleDate(booking?.data ?? "");
        setSelectedSlotId(booking?.slot_id ? String(booking.slot_id) : "");
      })
      .catch(() => setError("Não conseguimos carregar este lead."));
  }, [id]);

  useEffect(() => {
    if (!scheduleDate) {
      setDaySlots([]);
      return;
    }

    let alive = true;
    setLoadingSlots(true);
    apiFetch<{ slots: SlotOption[] }>(`/admin/slots?from=${scheduleDate}&to=${scheduleDate}`, { auth: "admin" })
      .then((res) => {
        if (!alive) return;
        setDaySlots(res.slots);
        const currentStillExists = res.slots.some((s) => String(s.id) === selectedSlotId);
        if (!currentStillExists) setSelectedSlotId("");
      })
      .catch(() => {
        if (alive) toast("Erro ao carregar horarios", true);
      })
      .finally(() => {
        if (alive) setLoadingSlots(false);
      });

    return () => {
      alive = false;
    };
  }, [scheduleDate, selectedSlotId]);

  const updateStatus = async (next: StatusKey) => {
    if (!lead) return;
    try {
      await apiFetch(`/admin/leads/${lead.id}`, {
        method: "PATCH",
        body: { status: next },
        auth: "admin",
      });
      setLead({ ...lead, status: next });
      toast("Status atualizado");
    } catch {
      toast("Erro ao atualizar status", true);
    }
  };

  const saveNotes = async () => {
    if (!lead) return;
    try {
      await apiFetch(`/admin/leads/${lead.id}`, {
        method: "PATCH",
        body: { notas: notes },
        auth: "admin",
      });
      setLead({ ...lead, notas: notes });
      toast("Notas salvas");
    } catch {
      toast("Erro ao salvar notas", true);
    }
  };

  const updatePriority = async (next: PriorityKey) => {
    if (!lead) return;
    try {
      await apiFetch(`/admin/leads/${lead.id}`, {
        method: "PATCH",
        body: { prioridade: next },
        auth: "admin",
      });
      setLead({ ...lead, prioridade: next });
      toast("Prioridade atualizada");
    } catch {
      toast("Erro ao atualizar prioridade", true);
    }
  };

  const saveContact = async () => {
    if (!lead) return;
    try {
      await apiFetch(`/admin/leads/${lead.id}`, {
        method: "PATCH",
        body: contactDraft,
        auth: "admin",
      });
      setLead({ ...lead, ...contactDraft });
      toast("Dados atualizados");
    } catch {
      toast("Erro ao atualizar dados", true);
    }
  };

  const cancelBooking = async () => {
    if (!lead || !booking) return;
    const ok = window.confirm("Cancelar este agendamento? O horario fica disponivel novamente.");
    if (!ok) return;
    try {
      await apiFetch(`/admin/leads/${lead.id}/booking`, {
        method: "DELETE",
        auth: "admin",
      });
      setBooking({ ...booking, status: "cancelado" });
      setLead({ ...lead, status: lead.status === "agendado" ? "contatado" : lead.status });
      toast("Agendamento cancelado");
    } catch {
      toast("Erro ao cancelar agendamento", true);
    }
  };

  const saveBooking = async () => {
    if (!lead || !selectedSlotId) return;
    setSavingBooking(true);
    try {
      const res = await apiFetch<{ booking: Booking }>(`/admin/leads/${lead.id}/booking`, {
        method: "PATCH",
        body: { slot_id: Number(selectedSlotId) },
        auth: "admin",
      });
      setBooking(res.booking);
      setLead({ ...lead, status: "agendado" });
      toast("Agendamento atualizado");
    } catch (e) {
      if (e instanceof ApiError && e.message === "slot_full") {
        toast("Horario ja esta cheio", true);
      } else if (e instanceof ApiError && e.message === "slot_unavailable") {
        toast("Horario indisponivel", true);
      } else {
        toast("Erro ao atualizar agendamento", true);
      }
    } finally {
      setSavingBooking(false);
    }
  };

  const deleteLead = async () => {
    if (!lead) return;
    const ok = window.confirm(`Excluir definitivamente o lead "${lead.nome}"? Essa acao nao pode ser desfeita.`);
    if (!ok) return;
    try {
      await apiFetch(`/admin/leads/${lead.id}`, {
        method: "DELETE",
        auth: "admin",
      });
      toast("Lead excluido");
      navigate("/admin/leads");
    } catch {
      toast("Erro ao excluir lead", true);
    }
  };

  const copySummary = async () => {
    if (!lead) return;
    const when =
      booking && booking.data
        ? `${fmtDate(booking.data)} ${fmtTime(booking.inicio)}-${fmtTime(booking.fim)}`
        : "sem agendamento";
    const text = [
      `Lead UdiSchool: ${lead.nome}`,
      `Email: ${lead.email}`,
      `WhatsApp: ${lead.whatsapp}`,
      `Status: ${STATUS_LABEL[lead.status]}`,
      `Prioridade: ${PRIORITY_LABEL[lead.prioridade]}`,
      `Agendamento: ${when}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(text);
      toast("Resumo copiado");
    } catch {
      toast("Nao foi possivel copiar", true);
    }
  };

  const toast = (msg: string, err = false) => {
    setSavedToast(`${err ? "err:" : "ok:"}${msg}`);
    setTimeout(() => setSavedToast(null), 2400);
  };

  if (error) return <div className="adm-login-error">{error}</div>;
  if (!lead) return <p style={{ color: "var(--ink-3)" }}>Carregando…</p>;

  const questionCount = Math.max(
    CONTENT.p3.questions.length,
    ...answers.map((a) => a.question_idx + 1),
    0,
  );

  return (
    <>
      <div className="adm-topbar">
        <button
          type="button"
          className="btn btn-ghost adm-back-btn"
          onClick={() => navigate("/admin/leads")}
        >
          <IconChevronL />
          Voltar
        </button>
      </div>

      <div className="adm-detail-head">
        <div>
          <h1 className="adm-detail-name">{lead.nome}</h1>
          <div className="adm-detail-meta">
            <a href={`mailto:${lead.email}`}>{lead.email}</a>
            <span>WhatsApp: {lead.whatsapp}</span>
            <span>Criado em {fmtDate(lead.created_at.split(" ")[0] ?? null)}</span>
          </div>
        </div>
        <div className="adm-detail-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={copySummary}
            style={{ padding: "12px 22px", fontSize: 14 }}
          >
            Copiar resumo
          </button>
          <a
            className="btn btn-whats"
            href={waUrl(lead.whatsapp)}
            target="_blank"
            rel="noopener"
            style={{ padding: "12px 22px", fontSize: 14 }}
          >
            <IconWhats /> Abrir WhatsApp
          </a>
        </div>
      </div>

      <div className="adm-detail-grid">
        <div>
          <div className="adm-panel" style={{ marginBottom: 18 }}>
            <h3 className="adm-panel-title" style={{ marginBottom: 14 }}>Respostas do funil</h3>
            {answers.length === 0 ? (
              <p className="adm-answer-empty">O lead ainda não respondeu o questionário.</p>
            ) : (
              Array.from({ length: questionCount }).map((_, idx) => {
                const a = answers.find((x) => x.question_idx === idx);
                const fallback = CONTENT.p3.questions[idx]?.[0] ?? "Pergunta removida do formulario atual";
                const questionText = a?.question_text || fallback;
                const value =
                  a?.answer_text ??
                  a?.answer_choice ??
                  (a?.answer_scale != null ? `${a.answer_scale} / 10` : null);
                return (
                  <div key={idx} className="adm-answer">
                    <div className="adm-answer-q">
                      {String(idx + 1).padStart(2, "0")} · {questionText}
                    </div>
                    {value ? (
                      <div className="adm-answer-a">{value}</div>
                    ) : (
                      <div className="adm-answer-empty">— sem resposta —</div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="adm-panel" style={{ marginBottom: 18 }}>
            <div className="adm-panel-head">
              <h3 className="adm-panel-title">Dados do lead</h3>
              <button type="button" className="btn btn-primary adm-mini-btn" onClick={saveContact}>
                Salvar dados
              </button>
            </div>
            <div className="adm-contact-grid">
              <label className="adm-field-block">
                <span>Nome</span>
                <input
                  className="adm-input"
                  value={contactDraft.nome}
                  onChange={(e) => setContactDraft({ ...contactDraft, nome: e.target.value })}
                  maxLength={160}
                />
              </label>
              <label className="adm-field-block">
                <span>Email</span>
                <input
                  className="adm-input"
                  type="email"
                  value={contactDraft.email}
                  onChange={(e) => setContactDraft({ ...contactDraft, email: e.target.value })}
                  maxLength={190}
                />
              </label>
              <label className="adm-field-block">
                <span>WhatsApp</span>
                <input
                  className="adm-input"
                  inputMode="tel"
                  value={contactDraft.whatsapp}
                  onChange={(e) => setContactDraft({ ...contactDraft, whatsapp: e.target.value })}
                  maxLength={40}
                />
              </label>
            </div>
          </div>

          <div className="adm-panel">
            <h3 className="adm-panel-title" style={{ marginBottom: 10 }}>Notas internas</h3>
            <textarea
              className="adm-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anotações sobre este lead…"
              rows={5}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <button type="button" className="btn btn-primary" onClick={saveNotes} style={{ padding: "10px 20px", fontSize: 14 }}>
                Salvar notas
              </button>
            </div>
          </div>
        </div>

        <aside>
          <div className="adm-side-card">
            <h4>Status</h4>
            <p style={{ marginTop: -8 }}>
              <span className={`adm-pill s-${lead.status}`}>{STATUS_LABEL[lead.status]}</span>
            </p>
            <div className="adm-status-actions">
              {STATUS_ORDER.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`adm-status-btn ${lead.status === s ? "active" : ""}`}
                  onClick={() => updateStatus(s)}
                >
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>

          <div className="adm-side-card">
            <h4>Prioridade</h4>
            <p style={{ marginTop: -8 }}>
              <span className={`adm-pill p-${lead.prioridade}`}>{PRIORITY_LABEL[lead.prioridade]}</span>
            </p>
            <div className="adm-status-actions">
              {PRIORITY_ORDER.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`adm-status-btn ${lead.prioridade === p ? "active" : ""}`}
                  onClick={() => updatePriority(p)}
                >
                  {PRIORITY_LABEL[p]}
                </button>
              ))}
            </div>
          </div>

          <div className="adm-side-card">
            <h4>Agendamento</h4>
            {booking && booking.data ? (
              <dl>
                <div className="adm-side-row"><dt>Data</dt><dd>{fmtDate(booking.data)}</dd></div>
                <div className="adm-side-row"><dt>Horário</dt><dd>{fmtTime(booking.inicio)} às {fmtTime(booking.fim)}</dd></div>
                <div className="adm-side-row"><dt>Status</dt><dd>{booking.status}</dd></div>
              </dl>
            ) : (
              <p className="adm-answer-empty">Sem agendamento.</p>
            )}
            <div className="adm-booking-editor">
              <label className="adm-field-block">
                <span>Nova data</span>
                <input
                  className="adm-input"
                  type="date"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                />
              </label>
              <label className="adm-field-block">
                <span>Horario</span>
                <select
                  className="adm-select"
                  value={selectedSlotId}
                  onChange={(e) => setSelectedSlotId(e.target.value)}
                  disabled={!scheduleDate || loadingSlots}
                >
                  <option value="">{loadingSlots ? "Carregando..." : "Escolha um horario"}</option>
                  {daySlots.map((s) => {
                    const isCurrent = booking?.slot_id === s.id;
                    const isFull = s.usados >= s.capacidade && !isCurrent;
                    const inactive = s.ativo !== 1;
                    return (
                      <option key={s.id} value={s.id} disabled={inactive || isFull}>
                        {fmtTime(s.inicio)} as {fmtTime(s.fim)}
                        {inactive ? " - inativo" : isFull ? " - cheio" : ` - ${s.usados}/${s.capacidade}`}
                      </option>
                    );
                  })}
                </select>
              </label>
              {scheduleDate && !loadingSlots && daySlots.length === 0 ? (
                <p className="adm-answer-empty">Nenhum horario cadastrado nesta data.</p>
              ) : null}
              <button
                type="button"
                className="adm-status-btn active adm-full-action"
                onClick={saveBooking}
                disabled={!selectedSlotId || savingBooking}
              >
                {savingBooking ? "Salvando..." : "Salvar agendamento"}
              </button>
            </div>
            {booking && booking.status !== "cancelado" ? (
              <button
                type="button"
                className="adm-status-btn danger adm-full-action"
                onClick={cancelBooking}
              >
                Cancelar agendamento
              </button>
            ) : null}
          </div>

          <div className="adm-side-card">
            <h4>Origem</h4>
            <dl>
              <div className="adm-side-row"><dt>UTM source</dt><dd>{lead.utm_source ?? "—"}</dd></div>
              <div className="adm-side-row"><dt>UTM medium</dt><dd>{lead.utm_medium ?? "—"}</dd></div>
              <div className="adm-side-row"><dt>UTM campaign</dt><dd>{lead.utm_campaign ?? "—"}</dd></div>
              <div className="adm-side-row"><dt>Canal</dt><dd>{lead.origem}</dd></div>
            </dl>
          </div>

          <div className="adm-side-card adm-danger-zone">
            <h4>Zona de risco</h4>
            <p>Use apenas para limpar lead duplicado ou criado por engano.</p>
            <button
              type="button"
              className="adm-status-btn danger adm-full-action"
              onClick={deleteLead}
            >
              Excluir lead
            </button>
          </div>
        </aside>
      </div>

      {savedToast && (
        <div className={`adm-toast ${savedToast.startsWith("err:") ? "err" : "ok"}`}>
          {savedToast.slice(3)}
        </div>
      )}
    </>
  );
}
